import type { ManagementQueryParams } from 'pages/Management/Management';

import { useCallback, useMemo } from 'react';

import TextCell from 'components/TextCell';
import LinkTo from 'components/LinkTo';
import { stringifyLocationSearch } from 'hooks/useStorage/utils';
import { useManagementPageContext } from 'pages/Management/contexts/ManagementPageContext';
import useFeatureFlag, { FEATURE_FLAGS } from 'utils/useFeatureFlag';

interface CountCellProps {
  count?: number;
  sectionPath: string;
  // When exp-pd1188 is enabled, we should change this type to
  // Omit<ManagementQueryParams, 'tab'> since the tab param will no longer be used in the
  //  URL or page state
  pageParams: ManagementQueryParams;
}

export default function CountCell({
  count = 0,
  sectionPath,
  pageParams,
}: CountCellProps) {
  const pd1188FFEnabled = useFeatureFlag(
    FEATURE_FLAGS.PD_1188_ROUTE_BASED_FLEET_MANAGEMENT,
  );

  const { setPageParams } = useManagementPageContext();

  const url = useMemo(() => {
    if (pd1188FFEnabled) {
      return `${sectionPath}${stringifyLocationSearch({
        oemSegmentIds: pageParams.oemSegmentIds,
        teamIds: pageParams.teamIds,
        accountIds: pageParams.accountIds,
      })}`;
    }

    return `/management${stringifyLocationSearch({
      tab: pageParams.tab,
      oemSegmentIds: pageParams.oemSegmentIds,
      teamIds: pageParams.teamIds,
      accountIds: pageParams.accountIds,
    })}`;
  }, [pageParams, pd1188FFEnabled, sectionPath]);

  const handleClick = useCallback(
    /* istanbul ignore next -- @preserve */
    () => {
      if (pd1188FFEnabled) {
        const { tab, ...routeBasedParams } = pageParams;
        setPageParams(routeBasedParams);
        return;
      }

      setPageParams(pageParams);
    },
    [pageParams, pd1188FFEnabled, setPageParams],
  );

  if (count) {
    return (
      // @ts-expect-error - JS File
      <LinkTo to={url} onClick={handleClick}>
        {count}
      </LinkTo>
    );
  }

  return <TextCell text={String(count)} />;
}
